import Box from '@app/components/Box';
import Flex from '@app/components/Flex';
import InputGroupSpinnerElement from '@app/components/InputGroupSpinnerElement';
import { FormGroup, Icon, InputGroup, Spinner } from '@blueprintjs/core';
import { Endpoints } from '@data/consts';
import { fetchClient } from '@pages/Clients/store/effects';
import React, { HTMLAttributes, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PlatformClientUserFilterQueryMode } from './enums';

export interface PlatformClientUserFilterQueryInputProps {
    loading: boolean;
    value: string;
    placeholder: string;
    shouldBeSearched: boolean;
    onChange: (e) => void;
    onSearch: (newSearch: string) => void;
}

export type Props = PlatformClientUserFilterQueryInputProps & HTMLAttributes<HTMLDivElement>;

const PlatformClientUserFilterQueryInput: React.FC<Props> = ({
    loading,
    value,
    shouldBeSearched,
    placeholder,
    onSearch,
    onChange,
    ...props
}) => {
    const navigate = useNavigate();

    const [mode, setMode] = useState(PlatformClientUserFilterQueryMode.NONE);
    const [hint, setHint] = useState('');

    const handleAssignHint = () => {
        switch (mode) {
            case PlatformClientUserFilterQueryMode.NONE:
                setHint(`Hint: type ‘#ClientId’ and press ‘enter’ to go directly to the client page`);
                break;
            case PlatformClientUserFilterQueryMode.JUMP_TO_CLIENT:
                const trimmedValue = value.trim();
                const clientUserId = trimmedValue.slice(1);
                setHint(`Hint: press 'enter' to jump to #${clientUserId} client page`);
                break;
        }
    };

    const handleSetModeExecution = () => {
        switch (mode) {
            case PlatformClientUserFilterQueryMode.JUMP_TO_CLIENT:
                const trimmedValue = value.trim();
                const clientUserId = trimmedValue.slice(1);
                fetchClient(clientUserId)
                    .then(() => {
                        navigate(Endpoints.CLIENT_TASKS.replace(':clientId', clientUserId));
                    })
                    .catch((e) => {
                        // TODO: handle error
                        console.error(e);
                    });
                break;
        }
    };

    const handleKeyPressed = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSetModeExecution();
        }
    };

    useEffect(() => {
        if (value.trim().length !== 0) {
            // Task ID - #NUMBER
            if (/^#\d+$/.test(value.trim())) {
                setMode(PlatformClientUserFilterQueryMode.JUMP_TO_CLIENT);
            } else {
                setMode(PlatformClientUserFilterQueryMode.NONE);
            }
        } else {
            setMode(PlatformClientUserFilterQueryMode.NONE);
            setHint(`Hint: type ‘#ClientId and press ‘enter’ to go directly to client page`);
        }
    }, [mode, value]);

    useEffect(() => {
        if (shouldBeSearched && mode === PlatformClientUserFilterQueryMode.NONE) {
            const timeoutId = setTimeout(() => {
                onSearch(value);
            }, 400);

            return () => {
                clearTimeout(timeoutId);
            };
        }

        handleAssignHint();

        // eslint-disable-next-line
    }, [mode, value]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPressed);

        return () => {
            document.removeEventListener('keydown', handleKeyPressed);
        };
    });

    const renderQueryInputElement = () => {
        if (loading) {
            return (
                <Box width="40px" height="41px">
                    <Flex fullWidth fullHeight align="center" justify="center">
                        <Spinner size={16} />
                    </Flex>
                </Box>
            );
        } else {
            return <Icon icon="search" size={16} />;
        }
    };

    return (
        <div {...props}>
            <FormGroup helperText={hint}>
                <InputGroup
                    large
                    placeholder={placeholder}
                    value={value}
                    leftElement={<InputGroupSpinnerElement loading={loading} />}
                    onChange={onChange}
                />
            </FormGroup>
        </div>
    );
};

export default PlatformClientUserFilterQueryInput;
